// src/hooks/usePermissions.js
import { useState, useEffect, useMemo } from 'react';
import { getLoggedInUser } from '../utils/authHelper';

/**
 * A custom hook to manage and check user permissions.
 * It reads the logged-in user's data and provides a quick way to verify permissions.
 *
 * @returns {{
 *  user: object | null,
 *  permissions: Set<string>,
 *  hasPermission: (permissionName: string) => boolean,
 *  isLoading: boolean
 * }}
 */
export const usePermissions = () => {
    const [user, setUser] = useState(null);
    const [isLoading, setIsLoading] = useState(true);

    useEffect(() => {
        const currentUser = getLoggedInUser();
        setUser(currentUser);
        setIsLoading(false);
    }, []);

    const permissions = useMemo(() => {
        if (!user || !user.role || !Array.isArray(user.role.permissions)) {
            return new Set();
        }
        // Store permissions in a Set for O(1) lookup time, which is much faster.
        const permissionTitles = user.role.permissions.map(p => p.title.toUpperCase());
        return new Set(permissionTitles);
    }, [user]);

    const hasPermission = (permissionName) => {
        if (!permissionName || isLoading) {
            return false;
        }
        return permissions.has(permissionName.toUpperCase());
    };

    return { user, permissions, hasPermission, isLoading };
};